import { Toaster } from '@/components/ui/toaster';
import { Box, Flex, Stack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { Navigate, Route, Routes } from 'react-router';
import './App.css';
import { AdminNav } from './components/admin/AdminNav';
import { AppHeader } from './components/app-header/app-header';
import { DevPanel } from './components/dev-panel';
import { Footer } from './components/footer';
import { GeocomplianceBanner } from './components/geocompliance-banner';
import { Navbar } from './components/navbar';
import { RoleProtectedRoute } from './components/role-protected-route';
import { ScrollToTop } from './components/scroll-to-top';
import { APP_ROUTES } from './constants/routes';
import { AppRoute, GEORESTRICTED_BANNER_HEIGHT, MAX_PAGE_WIDTH } from './constants/shared';
import { useAnalytics } from './hooks/useAnalytics';
import { useCompliance } from './hooks/useCompliance';
import { initDeviceId } from './hooks/useDeviceId';
import { useNetworkGuard } from './hooks/useNetworkGuard';
import { AirdropSummary } from './pages/Admin/Airdrop/AirdropSummary';
import { BattleGroupReview } from './pages/Admin/BattleGroups/BattleGroupReview';
import { PointsCompare } from './pages/Admin/Points/PointsCompare';
import { PortalStats } from './pages/Admin/PortalStats';
import { UserAdmin } from './pages/Admin/UserAdmin';
import { NotFound } from './pages/error/NotFound';
import { Home } from './pages/Home/Home';
import { Onboarding } from './pages/Onboarding/Onboarding';
import { Providers } from './providers/providers';
import { isAxiosLike } from './utils';
if (import.meta.env.VITE_SENTRY_DNS) {
  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DNS,
      integrations: [Sentry.browserTracingIntegration()],

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // ADJUST THIS AS NEEDED AS WE SCALE PROD
      tracesSampleRate: 1.0,
      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/.*-plume-network\.vercel\.app\/?$/, // All Plume Network Vercel deployments
        /^https:\/\/.*plumenetwork\.xyz$/, // Production domains
        'https://plume-hub-dev.vercel.app',
        'https://plume-hub.vercel.app',
      ],
      beforeSend(event, hint) {
        const error = hint?.originalException;

        // Duck-typing check for AxiosError
        if (isAxiosLike(error)) {
          const status = error.response?.status;
          const url = error.config?.url;

          if (status === 400 && url?.includes('api.relay.link/quote')) {
            return null; // Prevent it from being sent to Sentry
          }
        }

        return event;
      },
    });
  } catch (e) {
    console.error('Sentry failed to init', e);
  }
} else {
  console.warn('No sentry DNS, not initializing sentry');
}

initDeviceId();
function App() {
  return (
    <Providers>
      <ScrollToTop />
      <GeocomplianceBanner />
      <AppContent />
      <DevPanel />
      <Toaster />
    </Providers>
  );
}

const AppContent = () => {
  useAnalytics();
  useNetworkGuard();
  const { geoCompliance } = useCompliance();
  const restrictAccess = geoCompliance?.restrictAccess;
  return (
    <Box mt={restrictAccess ? GEORESTRICTED_BANNER_HEIGHT : 0}>
      <Routes>
        <Route path="onboarding/:currentStep" element={<Onboarding />} />
        <Route path="onboarding/" element={<Navigate to="/onboarding/welcome" replace />} />
        <Route path="/404" element={<NotFound />} />
        <Route path="admin/*" element={<AdminLayout />} />
        <Route
          path="/*"
          element={
            <Stack direction={['column', 'row']} minH={'100vh'} gap={0}>
              <Box
                w="266px"
                borderRight="1px solid"
                borderColor="border"
                bgColor="bg"
                h="100vh"
                overflow={['hidden', 'scroll']}
                scrollbar="visible"
                position="fixed"
                left={0}
                top={0}
                pl={6}
                pr={2}
                pt={6}
                display={['none', null, null, 'block']}
                mt={restrictAccess ? GEORESTRICTED_BANNER_HEIGHT : 0}
              >
                <Box h="full">
                  <Navbar />
                </Box>
              </Box>
              <Box display={['block', null, null, 'none']}>
                <AppHeader />
              </Box>

              <Routes>
                {APP_ROUTES.map(({ path, element: Element }) => {
                  if (!Element) return null; // if element is null, it's not an internal route
                  return (
                    <Route key={path} path={path} element={<WrappedRoute path={path} routeElement={<Element />} />} />
                  );
                })}

                <Route path="/*" element={<WrappedRoute path="/*" routeElement={<Home />} />} />
              </Routes>
            </Stack>
          }
        />
      </Routes>
    </Box>
  );
};

// Admin layout component for all admin pages
const AdminLayout = () => {
  return (
    <RoleProtectedRoute requiredRole="ADMIN">
      <Box py={8} px={[2, 2, 6]} w="full" h="full" overflow="hidden" bgColor="bg.accent">
        <Box m="auto" maxW={MAX_PAGE_WIDTH}>
          <AdminNav />
          <Routes>
            <Route path="points/compare" element={<PointsCompare />} />
            <Route path="battlegroups/review" element={<BattleGroupReview />} />
            <Route path="airdrop/summary" element={<AirdropSummary />} />
            <Route path="portal/stats" element={<PortalStats />} />
            <Route path="users" element={<UserAdmin />} />
            {/* Add more admin routes here */}
            <Route path="*" element={<Navigate to="/admin/points/compare" replace />} />
          </Routes>
        </Box>
      </Box>
    </RoleProtectedRoute>
  );
};

export default App;

const WrappedRoute = ({ path, routeElement }: { path: string; routeElement: React.ReactNode }) => {
  const isFullWidth = path === AppRoute.Bridge || path === AppRoute.Swap;

  return (
    <Flex
      py={8}
      px={[2, null, null, 6]}
      pl={[0, null, null, '266px']}
      w="full"
      h="full"
      minH="100vh"
      overflow="hidden"
      mt={{ base: 16, lg: 0 }}
      bgColor="bg.accent"
      direction="column"
    >
      <Box
        flex={1}
        w="full"
        m="auto"
        maxW={isFullWidth ? 'full' : MAX_PAGE_WIDTH}
        pl={[2, 2, 2, 6]}
        minH="full"
        display="grid"
        gridTemplateRows="1fr auto"
      >
        {routeElement}
        <Footer />
      </Box>
    </Flex>
  );
};
